import React, { memo, useEffect, useState, useRef, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { IconButton } from 'modules/ui';
import { loadAsset } from 'modules/assets';
import { getIsIOS } from 'modules/core';
import {
  setToPlaylist,
  getCurrentAudioUrl,
  setCurrentAudioUrl,
  makeGetPlayingByUrl,
  getPreviousAudioUrl,
  resetAudioUrls,
  setReadyToPlay,
  setBlobReadyToPlay,
  setAudioSampleKey,
  getAudioSampleKey,
} from '../redux';
import { isBlob } from '../services';
import styles from './AsyncPlayButton.module.scss';
import { flushSync } from 'react-dom';

const UNREGISTER_AFTER_FIRST_INVOCATION = {
  once: true,
};

const AsyncPlayButton = ({
  secondsElapsed,
  setSecondsElapsed,
  setTotalSeconds,
  fileUrl,
  token,
  onClick,
  audioSampleKey,
  className,
  disabled,
  containerClassName,
  fileName,
}) => {
  const dispatch = useDispatch();
  const audioRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [playingSource, setPlayingSource] = useState(null);
  const [audioLoaded, setAudioLoaded] = useState(false);
  const [downloadedSource, setDownloadedSource] = useState(null);

  const currentAudioUrl = useSelector(getCurrentAudioUrl);
  const lastAudioUrl = useSelector(getPreviousAudioUrl);
  const currentAudioSampleKey = useSelector(getAudioSampleKey);
  const isIOS = useSelector(getIsIOS);
  const getPlayingByUrl = makeGetPlayingByUrl(fileUrl);
  const shouldPlay = useSelector(
    state => getPlayingByUrl(state) && currentAudioSampleKey === audioSampleKey,
  );
  const firstTimePlaying = !currentAudioUrl && !lastAudioUrl;
  const nowPlayingUrl = currentAudioUrl;

  const setUrl = url => dispatch(setCurrentAudioUrl(url));
  const loadFile = (url, convertToUrl, token) =>
    dispatch(loadAsset({ assetPath: url, convertToUrl, token }));
  const toPlaylist = (currentAudioUrl, source) =>
    dispatch(setToPlaylist(currentAudioUrl, source));
  const handleSetReadyToPlay = isReadyToPlay =>
    dispatch(setReadyToPlay(isReadyToPlay));
  const handleSetReadyToPlayBlob = isReadyToPlayBlob =>
    dispatch(setBlobReadyToPlay(isReadyToPlayBlob));
  const handleAudioSampleKey = key => dispatch(setAudioSampleKey(key));

  useEffect(() => {
    const currentTime = Math.floor(audioRef.current.currentTime);
    const isSeeked = Math.abs(secondsElapsed - currentTime) > 0;
    if (isSeeked) {
      audioRef.current.currentTime = secondsElapsed;
    }

    if (!shouldPlay) {
      setLoading(false);
      setPlaying(false);

      if (audioRef.current) {
        audioRef.current.pause();
      }
    }
  }, [shouldPlay, secondsElapsed]);

  useEffect(() => {
    setAudioLoaded(false);
  }, [audioSampleKey]);

  useEffect(() => {
    registerAudioListeners();
    const audio = audioRef.current;

    return () => {
      if (playing || loading) {
        dispatch(resetAudioUrls());
        if (audio) {
          audio.pause();
        }
      }
      setLoading(false);
      setPlaying(false);
    };
  }, []);

  const handleClick = async event => {
    event.stopPropagation();

    const lastPlayingUrl = nowPlayingUrl;
    handleAudioSampleKey(audioSampleKey);

    if (!isIOS) {
      if (!playing) {
        setUrl(fileUrl);
        if (firstTimePlaying || !audioLoaded) {
          const media = await downloadMedia();
          playMedia(media, lastPlayingUrl);
        } else {
          playMedia(downloadedSource, lastPlayingUrl);
        }
      } else {
        pauseMedia();
      }
    } else if (!playing && !downloadedSource) {
      downloadMedia();
    } else if (!playing && downloadedSource) {
      if (nowPlayingUrl !== fileUrl) {
        await setUrl(fileUrl);
      }
      playMedia(downloadedSource, lastPlayingUrl);
    } else {
      pauseMedia();
    }

    if (onClick) {
      onClick();
    }
  };

  const registerAudioListeners = useCallback(() => {
    const trackCurrentTime = () => {
      const seconds = Math.floor(audioRef.current?.currentTime);
      if (seconds > secondsElapsed) {
        setSecondsElapsed(seconds);
      }
    };

    if (audioRef.current) {
      audioRef.current.addEventListener(
        'loadedmetadata',
        () => {
          setTotalSeconds(Math.floor(audioRef.current.duration));
        },
        UNREGISTER_AFTER_FIRST_INVOCATION,
      );

      audioRef.current.addEventListener(
        'ended',
        () => setSecondsElapsed(0),
        UNREGISTER_AFTER_FIRST_INVOCATION,
      );

      audioRef.current.addEventListener('timeupdate', trackCurrentTime);
    }
  }, [setSecondsElapsed, setTotalSeconds, secondsElapsed]);

  const playMedia = (url, lastPlayingUrl) => {
    registerAudioListeners();

    handleSetReadyToPlay(true);
    if (isBlob(fileUrl)) {
      handleSetReadyToPlayBlob(true);
    }

    // Note: React 18 batch updates require flushing at this point.
    // Audio source must be set in the DOM before calling load.
    flushSync(() => {
      setPlayingSource(url);
      toPlaylist({ fileUrl, url });
      setPlaying(true);
    });

    if (audioRef.current) {
      if (
        firstTimePlaying ||
        lastPlayingUrl !== nowPlayingUrl ||
        !audioLoaded
      ) {
        audioRef.current.load();
        setAudioLoaded(true);
      }
      const playPromise = audioRef.current.play();
      if (playPromise !== undefined) {
        playPromise.catch(error => {
          console.log('Audio play was prevented');
          console.log('error: ', error);
        });
      }
      audioRef.current.addEventListener('ended', pauseMedia);
    }
  };

  const downloadMedia = () => {
    handleSetReadyToPlay(false);
    setLoading(true);
    return loadFile(fileUrl, true, token).then(response => {
      const file = response.payload;
      setLoading(false);
      setDownloadedSource(file);
      setSecondsElapsed(0);
      return file;
    });
  };

  const pauseMedia = () => {
    audioRef.current.pause();
    setPlaying(false);
  };

  const getIconName = () => {
    if (isIOS && downloadedSource === null) {
      return 'downloadMedia';
    }
    return !playing ? 'play' : 'pause';
  };

  const iconName = getIconName();
  const btnClasses = classNames(styles.btn, className);

  return (
    <div className={containerClassName}>
      <audio ref={audioRef} preload="metadata">
        <source src={playingSource} type="audio/mp3" />
      </audio>
      <IconButton
        onClick={!loading ? handleClick : undefined}
        iconName={iconName}
        iconColor="white"
        className={btnClasses}
        loaderSize={15}
        loading={loading}
        disabled={disabled}
        size="large"
        aria-label={
          playing
            ? 'Pause audio sample ' + fileName
            : 'Play audio sample ' + fileName
        }
        aria-pressed={playing}
      />
    </div>
  );
};

AsyncPlayButton.propTypes = {
  topPlaylist: PropTypes.func,
  setCurrentAudioUrl: PropTypes.func,
  className: PropTypes.string,
  containerClassName: PropTypes.string,
  nowPlayingUrl: PropTypes.string,
  lastPlayingUrl: PropTypes.string,
  shouldPlay: PropTypes.bool,
  disabled: PropTypes.bool,
  token: PropTypes.string,
  isIOS: PropTypes.bool,
  handleResetAudioUrls: PropTypes.func,
  handleSetReadyToPlay: PropTypes.func,
  secondsElapsed: PropTypes.number,
  setSecondsElapsed: PropTypes.func,
  setTotalSeconds: PropTypes.func,
  audioSampleKey: PropTypes.string,
  handleAudioSampleKey: PropTypes.func,
};

export default memo(AsyncPlayButton);
